<template>
  <el-dialog
    v-model="dialogVisible"
    title="选择店铺"
    width="950px"
    :close-on-click-modal="false"
 >
    <el-form :model="searchForm" label-width="auto" inline size="small">
      <el-form-item label="店铺名称：">
        <el-input
          v-model="searchForm.keyword"
          class="form-item-width"
          placeholder="请输入店铺名称"
          clearable
          @keyup.enter="getMerchantList"
       />
      </el-form-item>
      <el-form-item label="店铺分类：">
        <el-select
          v-model="searchForm.categoryId"
          class="form-item-width"
          placeholder="请选择店铺分类"
          clearable
          @change="getMerchantList"
       >
          <el-option
            v-for="item in storeCateList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
         />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺类型：">
        <el-select
          v-model="searchForm.typeId"
          class="form-item-width"
          placeholder="请选择店铺类型"
          clearable
          @change="getMerchantList"
       >
          <el-option
            v-for="item in storeTypeList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
         />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="small" @click="() => getMerchantList()">搜索</el-button>
        <el-button size="small" @click="searchReset">重置</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="tableData.loading" :data="tableData.data" size="small" max-height="500">
      <el-table-column prop="merId" label="店铺ID" width="80">
        <template #default="{ row }">
          <el-checkbox
            v-if="multiple"
            :model-value="selectedMerIdList.includes(row.merId)"
           @update:model-value="(checked) => onToggleMer(row.merId, checked === true)"
          />
          <el-radio v-else v-model="selectedMerId" :value="row.merId" />
        </template>
      </el-table-column>
      <el-table-column prop="merName" label="店铺名称"  min-width="150"/>
      <el-table-column prop="merPhone" label="店铺电话"  min-width="120"/>
    </el-table>
    <div class="block">
      <el-pagination
        v-model:current-page="searchForm.page"
        v-model:page-size="searchForm.limit"
        layout="prev, pager, next, jumper"
        :total="tableData.total"
       @size-change="() => getMerchantList()"
        @current-change="() => getMerchantList()"
      />
    </div>
    <template #footer>
      <div>
        <el-button size="small" @click="handleCancel">取消</el-button>
        <el-button size="small" type="primary" @click="handleConfirm">确定</el-button>
      </div>
    </template>
  </el-dialog>
</template>


<script setup lang="ts">
//4.0
import { onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { merchantListApi, getMerCateApi, getstoreTypeApi } from '@/api/merchant';

defineOptions({ name: 'merSelect' });

interface SelectOption {
  value: string | number;
  label: string;
}

interface MerchantRow {
  merId: number;
  merName?: string;
  merPhone?: string;
}

const getSearchForm = () => ({
  status: 1,
  merState: 1, // 店铺状态
  page: 1,
  limit: 8,
  keyword: null as string | null,
  typeId: undefined as string | number | undefined,
  categoryId: undefined as string | number | undefined,
});

const props = withDefaults(
  defineProps<{
    autoLoad?: boolean;
 }>(),
  { autoLoad: true },
);

const emit = defineEmits<{
 (e: 'confirm', value: number | number[] | null): void;
}>();

const dialogVisible = ref(false);
const searchForm = reactive(getSearchForm());
const tableData = reactive({
  data: [] as MerchantRow[],
  total: 0,
  loading: false,
});
const multiple = ref(false);
const selectedMerIdList = ref<number[]>([]);
const selectedMerId = ref<number | undefined>(undefined);
const storeCateList = ref<SelectOption[]>([]);
const storeTypeList = ref<SelectOption[]>([]);

function onToggleMer(merId: number, checked: boolean) {
  if (checked) {
    if (!selectedMerIdList.value.includes(merId)) {
      selectedMerIdList.value = [...selectedMerIdList.value, merId];
    }
  } else {
    selectedMerIdList.value = selectedMerIdList.value.filter((id) => id !== merId);
  }
}

async function getStoreCategory() {
  try {
    const res = await getMerCateApi();
    storeCateList.value = (res as { data: SelectOption[] }).data;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    ElMessage.error(msg);
  }
}

async function getStoreType() {
  try {
    const res = await getstoreTypeApi();
    storeTypeList.value = (res as { data: SelectOption[] }).data;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    ElMessage.error(msg);
  }
}

function searchReset() {
  Object.assign(searchForm, getSearchForm());
  void getMerchantList();
}

function handleCancel() {
  dialogVisible.value = false;
}

function handleConfirm() {
  if (multiple.value) {
    emit('confirm', [...selectedMerIdList.value]);
  } else {
    const v = selectedMerId.value;
    emit('confirm', v === undefined ? null : v);
  }
  handleCancel();
}

async function getMerchantList(extendParams: Record<string, unknown> = {}) {
  if (tableData.loading) return;
  tableData.loading = true;
  try {
    const res = await merchantListApi({ ...searchForm, ...extendParams });
    const body = res as { data: { list: MerchantRow[]; count: number } };
    tableData.data = body.data.list;
    tableData.total = body.data.count;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    ElMessage.error(msg);
  } finally {
    tableData.loading = false;
  }
}

function open(multipleArg = false, selectedMerIdArg?: number | number[] | null, merState = null) {
  dialogVisible.value = true;
  multiple.value = multipleArg;
    searchForm.merState = merState;
  if (selectedMerIdArg !== undefined && selectedMerIdArg !== null) {
    if (multiple.value && Array.isArray(selectedMerIdArg)) {
      selectedMerIdList.value = [...selectedMerIdArg];
    } else if (!multiple.value && Number.isInteger(selectedMerIdArg)) {
      selectedMerId.value = selectedMerIdArg as number;
    }
  }
    getMerchantList();
}

onMounted(() => {
  if (props.autoLoad) {
    void getMerchantList();
  }
  void getStoreCategory();
  void getStoreType();
});

defineExpose({
  getMerchantList,
  open,
});
</script>

<style scoped lang="scss">
.form-item-width {
  width: 150px;
}
</style>
